import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageSquare, Heart, Reply, ArrowLeft, 
  MoreVertical, Send, Flame, Edit, Trash2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { createPortal } from 'react-dom';
import SimpleTextFormatter from '@/components/community/SimpleTextFormatter';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import SidebarLayout from '@/components/layout/SidebarLayout';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import DashboardCard from '@/components/ui/DashboardCard';
import AvatarPics from '@/components/ui/AvatarPics';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { getAssistantProfilePictureUrl } from '@/utils/image-utils';
import { useAuth } from '@/contexts/auth';
import { 
  useCreateReply,
  useToggleDiscussionLike,
  useToggleReplyLike,
  useUpdateReply,
  useDeleteReply,
  useUpdateDiscussion,
  useDeleteDiscussion
} from '@/hooks/community/use-community-discussions';
import { useDiscussionThreadData } from '@/hooks/community/use-discussion-thread-data';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import CommunityProfileLightbox from '@/components/community/CommunityProfileLightbox';
import { CommunityProfilePicture, CommunityUser, CommunityProfileContext } from '@/types/community-profile';
// AI responses now use the same ReplyCard component with role: 'AI'

// Enhanced text renderer that also handles @mentions
const renderContentWithMarkdown = (text: string) => {
  // Process mentions in the text
  const processMentions = (content: string): (string | React.ReactElement)[] => {
    const mentionRegex = /@(\w+)/g;
    const parts = content.split(mentionRegex);
    
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        // This is a mention (every odd index after split)
        return (
          <span 
            key={index} 
            className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1 py-0.5 rounded font-medium"
          >
            @{part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div className="prose-sm max-w-none text-slate-700 dark:text-slate-300 leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <SimpleTextFormatter 
        content={text} 
        className="[&>p]:my-1 [&>p]:first:mt-0 [&>p]:last:mb-0"
        mentionProcessor={processMentions}
      />
    </div>
  );
};

// Reply Card Component
const ReplyCard: React.FC<{
  reply: {
    id: string;
    content: string;
    author: { name: string; avatar?: string; initials: string; role?: string; userId?: string };
    timestamp: Date;
    likes: number;
    isLiked: boolean;
    isAuthor?: boolean;
    level: number;
    userId?: string; // Add userId to check ownership
    isAI?: boolean; // Add isAI flag to identify AI responses
  };
  onLike: (id: string) => void;
  onReply: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  currentUserId?: string;
  isEditing?: boolean;
  editContent?: string;
  onEditContentChange?: (content: string) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
}> = ({ 
  reply, 
  onLike, 
  onReply, 
  onEdit, 
  onDelete, 
  currentUserId,
  isEditing = false,
  editContent = '',
  onEditContentChange,
  onSaveEdit,
  onCancelEdit
}) => {
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [justLiked, setJustLiked] = useState(false);
  const moreOptionsRef = useRef<HTMLButtonElement>(null);
  
  // Profile lightbox state
  const [isProfileLightboxOpen, setIsProfileLightboxOpen] = useState(false);
  const [profileLightboxImage, setProfileLightboxImage] = useState<CommunityProfilePicture | null>(null);
  
  // Check if current user owns this reply (and it's not an AI response)
  const isOwnReply = !reply.isAI && reply.userId && currentUserId && reply.userId === currentUserId;

  const handleLike = () => {
    setJustLiked(true);
    onLike(reply.id);
    // Reset animation trigger after delay
    setTimeout(() => setJustLiked(false), 600);
  };
  
  const handleAvatarClick = () => {
    if (reply.author.avatar && reply.author.userId && !reply.isAI) {
      const profileImage: CommunityProfilePicture = {
        id: `profile-${reply.author.userId}`,
        url: reply.author.avatar,
        userId: reply.author.userId,
        userName: reply.author.name,
        alt: `${reply.author.name}'s profile picture`
      };
      
      setProfileLightboxImage(profileImage);
      setIsProfileLightboxOpen(true);
    }
  };
  
  const handleCloseLightbox = () => {
    setIsProfileLightboxOpen(false);
    setProfileLightboxImage(null);
  };
  
  return (
    <div className="group relative bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 border border-slate-200 dark:border-slate-700"
    >
      <div className="flex items-start gap-3">
        <AvatarPics
          profilePictureUrl={reply.author.avatar}
          initials={reply.author.initials}
          className="h-10 w-10 flex-shrink-0"
          onClick={handleAvatarClick}
          clickable={!!(reply.author.avatar && reply.author.userId && !reply.isAI)}
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {reply.author.name}
            </span>
            {reply.author.role && (
              <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                {reply.author.role}
              </span>
            )}
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {formatDistanceToNow(reply.timestamp, { addSuffix: true })}
            </span>
          </div>
          
          {/* Content or Edit Mode */}
          {isEditing ? (
            <div className="space-y-3">
              <Textarea
                value={editContent}
                onChange={(e) => onEditContentChange?.(e.target.value)}
                className="min-h-[100px] resize-none"
                placeholder="Edit your reply..."
              />
              <div className="flex gap-2">
                <Button
                  onClick={onSaveEdit}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save
                </Button>
                <Button
                  onClick={onCancelEdit}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
              {renderContentWithMarkdown(reply.content)}
            </div>
          )}
          
          {/* Actions */}
          {!isEditing && (
            <div className="flex items-center gap-4 mt-3">
              <motion.button
                onClick={handleLike}
                className={`flex items-center gap-1.5 text-sm transition-colors ${
                  reply.isLiked 
                    ? 'text-red-500 hover:text-red-600' 
                    : 'text-slate-500 hover:text-red-500'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  className="flex items-center justify-center relative"
                  animate={justLiked ? {
                    scale: [1, 1.4, 1.1, 1],
                    rotate: [0, -10, 10, 0],
                    transition: { 
                      duration: 0.6,
                      ease: [0.19, 1, 0.22, 1]
                    }
                  } : {}}
                >
                  <div className="relative">
                    <AnimatePresence mode="wait">
                      {reply.isLiked ? (
                        <motion.div
                          key="heart-filled"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "circOut" }}
                        >
                          <Heart 
                            size={16} 
                            className="text-red-500 fill-red-500"
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="heart-outline"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "circOut" }}
                        >
                          <Heart size={16} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    {/* Pulsing ring effect when toggled to favorite */}
                    {justLiked && reply.isLiked && (
                      <motion.div
                        className="absolute inset-0 rounded-full border-2 border-red-500/70"
                        initial={{ scale: 0.8, opacity: 0.7 }}
                        animate={{ scale: 1.8, opacity: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                      />
                    )}
                  </div>
                </motion.div>
                <motion.span
                  key={reply.likes} // Ensures animation on count change
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  {reply.likes || 0}
                </motion.span>
              </motion.button>
              
              <button
                onClick={() => onReply(reply.id)}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-blue-500 transition-colors"
              >
                <Reply size={16} />
                Reply
              </button>
            </div>
          )}
        </div>
        
        {/* Only show more options for user's own replies (not AI responses) */}
        {isOwnReply && !isEditing && (
          <div className="relative">
            <button 
              ref={moreOptionsRef}
              onClick={() => setShowMoreOptions(!showMoreOptions)}
              className="opacity-0 group-hover:opacity-100 p-2 rounded-full hover:bg-white/20 dark:hover:bg-black/20 transition-all"
              title="More options"
            >
              <MoreVertical size={16} className="text-slate-400" />
            </button>
            
            <MoreOptionsMenu
              isOpen={showMoreOptions}
              onClose={() => setShowMoreOptions(false)}
              onEdit={() => onEdit?.(reply.id)}
              onDelete={() => onDelete?.(reply.id)}
              triggerRef={moreOptionsRef}
            />
          </div>
        )}
      </div>
      
      {/* Community Profile Lightbox */}
      {profileLightboxImage && (
        <CommunityProfileLightbox
          currentImage={profileLightboxImage}
          isLightboxOpen={isProfileLightboxOpen}
          profileContext={{
            user: {
              id: reply.author.userId || '',
              first_name: reply.author.name,
              profile_picture_url: reply.author.avatar
            },
            replyId: reply.id,
            isAuthor: reply.isAuthor
          }}
          onClose={handleCloseLightbox}
        />
      )}
    </div>
  );
};

// Reply Composer Component
const ReplyComposer: React.FC<{
  onSubmit: (content: string) => void;
  placeholder?: string;
  mentionText?: string;
  onMentionAdded?: () => void;
}> = ({ onSubmit, placeholder = "Share your thoughts...", mentionText, onMentionAdded }) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add mention text when it changes
  React.useEffect(() => {
    if (mentionText && mentionText !== content) {
      setContent(mentionText);
      onMentionAdded?.();
      // Focus the textarea after adding mention
      setTimeout(() => {
        const textarea = document.querySelector('#reply-composer textarea') as HTMLTextAreaElement;
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(mentionText.length, mentionText.length);
        }
      }, 100);
    }
  }, [mentionText, onMentionAdded]);

  const handleSubmit = () => {
    if (!content.trim()) return;
    
    setIsSubmitting(true);
    onSubmit(content);
    setContent('');
    // Reset submitting state after a short delay to show feedback
    setTimeout(() => setIsSubmitting(false), 300);
  };

  return (
    <DashboardCard className="mt-6" id="reply-composer">
      <div className="space-y-4">
        <Textarea
          id="reply-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          className="min-h-[100px] resize-none"
        />
        
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {content.length}/2000 characters
          </div>
          
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button 
              onClick={handleSubmit}
              disabled={!content.trim() || isSubmitting}
              size="sm"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={14} className="mr-1" />
                  Post Reply
                </>
              )}
            </Button>
          </motion.div>
        </div>
      </div>
    </DashboardCard>
  );
};

// Post Sidebar Component
const PostSidebar: React.FC<{
  onReply: () => void;
}> = ({ onReply }) => {
  return (
    <div className="space-y-3 md:space-y-6">
      <div className="relative p-6 rounded-lg overflow-hidden">
        {/* Background with glassmorphism */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/30 to-transparent dark:via-blue-500/20 rounded-t-lg" />
        <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />

        <motion.button
          whileHover={{ scale: 1.02, x: 4 }}
          whileTap={{ scale: 0.98 }}
          onClick={onReply}
          className="w-full p-3 rounded-lg cursor-pointer transition-all duration-200 group
            bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700
            text-white font-medium"
        >
          <div className="flex items-center gap-3">
            <Reply size={16} />
            <span>Post a Reply</span>
          </div>
        </motion.button>
      </div>
    </div>
  );
};

// More Options Menu Component
const MoreOptionsMenu: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}> = ({ isOpen, onClose, onEdit, onDelete, triggerRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 160; // Approximate menu width
      const menuHeight = 80; // Approximate menu height
      
      let left = rect.right - menuWidth;
      let top = rect.bottom + 4;
      
      // Adjust if menu would go off the right edge
      if (left < 8) {
        left = 8;
      }
      
      // Adjust if menu would go off the bottom edge
      if (top + menuHeight > window.innerHeight - 8) {
        top = rect.top - menuHeight - 4;
      }
      
      setPosition({ top, left });
    }
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      {/* Menu */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        style={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          zIndex: 50,
        }}
        className="w-40 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1"
      >
        <button
          onClick={() => {
            onEdit();
            onClose();
          }}
          className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
        >
          <Edit size={14} />
          Edit
        </button>
        <button
          onClick={() => {
            onDelete();
            onClose();
          }}
          className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
        >
          <Trash2 size={14} />
          Delete
        </button>
      </motion.div>
    </>,
    document.body
  );
};

// Reply Item Component that handles individual reply logic
const ReplyItem: React.FC<{
  reply: any;
  discussion: any;
  onLike: (id: string) => void;
  onReply: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  currentUserId?: string;
  isEditing: boolean;
  editContent: string;
  onEditContentChange: (content: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  getUserName: (firstName?: string) => string;
  getInitials: (firstName?: string) => string;
}> = ({
  reply,
  discussion,
  onLike,
  onReply,
  onEdit,
  onDelete,
  currentUserId,
  isEditing,
  editContent,
  onEditContentChange,
  onSaveEdit,
  onCancelEdit,
  getUserName,
  getInitials
}) => {
  // The new data structure already includes like status from the PostgreSQL function
  const replyLikeStatus = reply.is_liked_by_user;
  
  if (reply.is_ai_response && reply.assistant_id) {
    // Get assistant info from the ai_assistant field
    const assistantData = reply.ai_assistant;
    
    // Transform AI reply data to use the same ReplyCard component
    const transformedAIReply = {
      id: reply.id,
      content: reply.content,
      author: {
        name: assistantData?.name || 'AI Assistant',
        avatar: assistantData?.profile_picture_url,
        initials: assistantData?.name?.slice(0, 2).toUpperCase() || 'AI',
        role: 'AI'
      },
      timestamp: new Date(reply.created_at),
      likes: reply.likes || 0,
      isLiked: replyLikeStatus,
      isAuthor: false,
      level: 0,
      userId: reply.user_id,
      isAI: true // Mark as AI to prevent more options
    };
    
    return (
      <ReplyCard
        reply={transformedAIReply}
        onLike={onLike}
        onReply={onReply}
        onEdit={onEdit}
        onDelete={onDelete}
        currentUserId={currentUserId}
        isEditing={isEditing}
        editContent={editContent}
        onEditContentChange={onEditContentChange}
        onSaveEdit={onSaveEdit}
        onCancelEdit={onCancelEdit}
      />
    );
  } else {
    // Transform regular reply data to match ReplyCard interface
    const transformedReply = {
      id: reply.id,
      content: reply.content,
      author: {
        name: getUserName(reply.author?.first_name),
        avatar: reply.author?.profile_picture_url,
        initials: getInitials(reply.author?.first_name),
        role: undefined,
        userId: reply.author?.id
      },
      timestamp: new Date(reply.created_at),
      likes: reply.likes || 0,
      isLiked: replyLikeStatus,
      isAuthor: reply.user_id === discussion.user_id,
      level: 0,
      userId: reply.user_id,
      isAI: false // Mark as human reply
    };
    
    return (
      <ReplyCard
        reply={transformedReply}
        onLike={onLike}
        onReply={onReply}
        onEdit={onEdit}
        onDelete={onDelete}
        currentUserId={currentUserId}
        isEditing={isEditing}
        editContent={editContent}
        onEditContentChange={onEditContentChange}
        onSaveEdit={onSaveEdit}
        onCancelEdit={onCancelEdit}
      />
    );
  }
};

// Main Post Thread Component
const PostThread: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [mentionText, setMentionText] = useState<string>('');
  const [showPostMoreOptions, setShowPostMoreOptions] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [replyToDelete, setReplyToDelete] = useState<string | null>(null);
  const [justLikedPost, setJustLikedPost] = useState(false);
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editPostTitle, setEditPostTitle] = useState<string>('');
  const [editPostContent, setEditPostContent] = useState<string>('');
  const [showDeletePostDialog, setShowDeletePostDialog] = useState(false);
  const postMoreOptionsRef = useRef<HTMLButtonElement>(null);
  
  // Profile lightbox state for main post
  const [isMainPostProfileLightboxOpen, setIsMainPostProfileLightboxOpen] = useState(false);
  const [mainPostProfileLightboxImage, setMainPostProfileLightboxImage] = useState<CommunityProfilePicture | null>(null);
  
  const { user } = useAuth();

  // Fetch discussion and replies data using the optimized PostgreSQL function
  const { data: threadData, isLoading: discussionLoading, error: discussionError } = useDiscussionThreadData(id || '');
  
  // Extract discussion and replies from the thread data
  const discussion = threadData?.discussion;
  const replies = threadData?.replies || [];
  const isLiked = discussion?.is_liked_by_user || false;
  
  // Mutations
  const toggleDiscussionLike = useToggleDiscussionLike();
  const toggleReplyLike = useToggleReplyLike();
  const createReply = useCreateReply();
  const updateReply = useUpdateReply();
  const deleteReply = useDeleteReply();
  const updateDiscussion = useUpdateDiscussion();
  const deleteDiscussion = useDeleteDiscussion();

  // Helper functions
  const getInitials = (firstName?: string) => {
    if (firstName && firstName.length >= 2) {
      return firstName.slice(0, 2).toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase() + 'U';
    }
    return 'U';
  };
  
  const getUserName = (firstName?: string) => {
    if (firstName) {
      return firstName;
    }
    return 'Anon';
  };

  // Check if current user owns the main post
  const isOwnPost = discussion && user && discussion.user_id === user.id;

  const handleEditPost = () => {
    if (discussion) {
      setEditPostTitle(discussion.title);
      setEditPostContent(discussion.content);
      setIsEditingPost(true);
    }
  };

  const handleSavePostEdit = () => {
    if (id && editPostTitle.trim() && editPostContent.trim()) {
      updateDiscussion.mutate({
        discussionId: id,
        title: editPostTitle.trim(),
        content: editPostContent.trim()
      });
      setIsEditingPost(false);
      setEditPostTitle('');
      setEditPostContent('');
    }
  };

  const handleCancelPostEdit = () => {
    setIsEditingPost(false);
    setEditPostTitle('');
    setEditPostContent('');
  };

  const handleDeletePost = () => {
    setShowDeletePostDialog(true);
  };

  const handleConfirmDeletePost = () => {
    if (id) {
      deleteDiscussion.mutate(id, {
        onSuccess: () => {
          // Navigate back to community after successful deletion
          navigate('/community');
        }
      });
    }
    setShowDeletePostDialog(false);
  };

  const handleCancelDeletePost = () => {
    setShowDeletePostDialog(false);
  };

  const handleEditReply = (replyId: string) => {
    const reply = replies.find(r => r.id === replyId);
    if (reply) {
      setEditingReplyId(replyId);
      setEditContent(reply.content);
    }
  };

  const handleSaveEdit = () => {
    if (editingReplyId && id) {
      updateReply.mutate({
        replyId: editingReplyId,
        content: editContent,
        discussionId: id
      });
      setEditingReplyId(null);
      setEditContent('');
    }
  };

  const handleCancelEdit = () => {
    setEditingReplyId(null);
    setEditContent('');
  };

  const handleDeleteReply = (replyId: string) => {
    setReplyToDelete(replyId);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (replyToDelete && id) {
      deleteReply.mutate({
        replyId: replyToDelete,
        discussionId: id
      });
      setShowDeleteDialog(false);
      setReplyToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setReplyToDelete(null);
  };

  if (discussionLoading) {
    return (
      <MobilePageWrapper>
        <Container>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </Container>
      </MobilePageWrapper>
    );
  }

  if (discussionError || !discussion) {
    return (
      <MobilePageWrapper>
        <Container>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-600 dark:text-red-400">Post not found</p>
              <Button onClick={() => navigate('/community')} className="mt-4">
                Return to Community
              </Button>
            </div>
          </div>
        </Container>
      </MobilePageWrapper>
    );
  }

  const handleLike = () => {
    if (id) {
      setJustLikedPost(true);
      toggleDiscussionLike.mutate(id);
      // Reset animation trigger after delay
      setTimeout(() => setJustLikedPost(false), 600);
    }
  };

  const handleReplyLike = (replyId: string) => {
    if (id) {
      toggleReplyLike.mutate({ replyId, discussionId: id });
    }
  };

  const handleReply = (replyId: string) => {
    // Get the reply to determine if it's AI or user
    const reply = replies.find(r => r.id === replyId);
    if (!reply) return;
    
    let mentionName = '';
    if (reply.is_ai_response && reply.ai_assistant?.name) {
      // For AI responses, use the assistant name
      mentionName = reply.ai_assistant.name;
    } else if (reply.author?.first_name) {
      // For user responses, use the user's first name
      mentionName = reply.author.first_name;
    } else {
      // Fallback for anonymous users
      mentionName = 'Anon';
    }
    
    setMentionText(`@${mentionName} `);
    
    // Scroll to reply input
    const replyInput = document.getElementById('reply-input');
    if (replyInput) {
      replyInput.focus();
      replyInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSubmitReply = (content: string) => {
    if (id) {
      createReply.mutate({ discussionId: id, content });
    }
  };

  const handleSidebarReply = () => {
    // Scroll to reply input
    const replyInput = document.getElementById('reply-input');
    if (replyInput) {
      replyInput.focus();
      replyInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  const handleMainPostAvatarClick = () => {
    if (discussion?.author?.profile_picture_url && discussion?.author?.id) {
      const profileImage: CommunityProfilePicture = {
        id: `profile-${discussion.author.id}`,
        url: discussion.author.profile_picture_url,
        userId: discussion.author.id,
        userName: getUserName(discussion.author.first_name),
        alt: `${getUserName(discussion.author.first_name)}'s profile picture`
      };
      
      setMainPostProfileLightboxImage(profileImage);
      setIsMainPostProfileLightboxOpen(true);
    }
  };
  
  const handleMainPostCloseLightbox = () => {
    setIsMainPostProfileLightboxOpen(false);
    setMainPostProfileLightboxImage(null);
  };

  return (
    <MobilePageWrapper>
      <Container>
        <DashboardHeader 
          title="Community Post"
          description={`Post: ${discussion.title}`}
          primaryAction={{
            label: "See All Posts",
            icon: MessageSquare,
            onClick: () => navigate('/community'),
            variant: "secondary"
          }}
        />
        <SidebarLayout
          sidebar={
            <PostSidebar
              onReply={handleSidebarReply}
            />
          }
        >
          <div className="space-y-3 md:space-y-6">
            {/* Main Post */}
            <DashboardCard>
              <div className="group relative bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 border border-slate-200 dark:border-slate-700"
              >
                <div className="flex items-start gap-3">
                  <AvatarPics
                    profilePictureUrl={discussion?.author?.profile_picture_url}
                    initials={getInitials(discussion?.author?.first_name)}
                    className="h-10 w-10 flex-shrink-0"
                    onClick={handleMainPostAvatarClick}
                    clickable={!!(discussion?.author?.profile_picture_url && discussion?.author?.id)}
                  />
                  
                  <div className="flex-1 min-w-0">
                    {/* Author info at the top */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {getUserName(discussion?.author?.first_name)}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                        General
                      </span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        {formatDistanceToNow(new Date(discussion?.created_at || ''), { addSuffix: true })}
                      </span>
                    </div>
                    
                    {/* Post content or Edit Mode */}
                    {isEditingPost ? (
                      <div className="space-y-3 mb-3">
                        <Input
                          value={editPostTitle}
                          onChange={(e) => setEditPostTitle(e.target.value)}
                          placeholder="Discussion title..."
                          className="font-medium"
                        />
                        <Textarea
                          value={editPostContent}
                          onChange={(e) => setEditPostContent(e.target.value)}
                          className="min-h-[100px] resize-none"
                          placeholder="Edit your post content..."
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSavePostEdit}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={!editPostTitle.trim() || !editPostContent.trim()}
                          >
                            Save Changes
                          </Button>
                          <Button
                            onClick={handleCancelPostEdit}
                            size="sm"
                            variant="outline"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed whitespace-pre-wrap mb-3">
                        {renderContentWithMarkdown(discussion?.content || '')}
                      </div>
                    )}
                    
                    {/* Actions */}
                    {!isEditingPost && (
                      <div className="flex items-center gap-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleLike}
                        className={`flex items-center gap-1.5 text-sm transition-colors ${
                          isLiked 
                            ? 'text-red-500 hover:text-red-600' 
                            : 'text-slate-500 hover:text-red-500'
                        }`}
                      >
                        <motion.div
                          className="flex items-center justify-center relative"
                          animate={justLikedPost ? {
                            scale: [1, 1.4, 1.1, 1],
                            rotate: [0, -10, 10, 0],
                            transition: { 
                              duration: 0.6,
                              ease: [0.19, 1, 0.22, 1]
                            }
                          } : {}}
                        >
                          <div className="relative">
                            <AnimatePresence mode="wait">
                              {isLiked ? (
                                <motion.div
                                  key="heart-filled"
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{ duration: 0.3, ease: "circOut" }}
                                >
                                  <Heart size={16} className="text-red-500 fill-red-500" />
                                </motion.div>
                              ) : (
                                <motion.div
                                  key="heart-outline"
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{ duration: 0.3, ease: "circOut" }}
                                >
                                  <Heart size={16} />
                                </motion.div>
                              )}
                            </AnimatePresence>
                            {/* Pulsing ring effect when toggled to favorite */}
                            {justLikedPost && isLiked && (
                              <motion.div
                                className="absolute inset-0 rounded-full border-2 border-red-500/70"
                                initial={{ scale: 0.8, opacity: 0.7 }}
                                animate={{ scale: 1.8, opacity: 0 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                              />
                            )}
                          </div>
                        </motion.div>
                        <motion.span
                          key={discussion.likes} // Ensures animation on count change
                          initial={{ y: -10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        >
                          {discussion.likes || 0}
                        </motion.span>
                      </motion.button>
                      
                      <button
                        onClick={handleSidebarReply}
                        className="flex items-center gap-1 text-sm text-slate-500 hover:text-blue-500 transition-colors"
                      >
                        <MessageSquare size={16} />
                        {discussion.replies || 0}{discussion.replies === 1 ? ' reply' : discussion.replies > 1 ? ' replies' : ''}
                      </button>
                      
                      {/* Hot indicator */}
                      {discussion.is_hot && (
                        <div className="flex items-center gap-1 ml-auto mr-2">
                          <Flame className="h-4 w-4 text-orange-500" />
                        </div>
                      )}
                      </div>
                    )}
                  </div>
                  
                  {/* Only show more options for post owner when not editing */}
                  {isOwnPost && !isEditingPost && (
                    <div className="relative">
                      <button 
                        ref={postMoreOptionsRef}
                        onClick={() => setShowPostMoreOptions(!showPostMoreOptions)}
                        className="opacity-0 group-hover:opacity-100 p-2 rounded-full hover:bg-white/20 dark:hover:bg-black/20 transition-all"
                        title="More options"
                      >
                        <MoreVertical size={16} className="text-slate-400" />
                      </button>
                      
                      <MoreOptionsMenu
                        isOpen={showPostMoreOptions}
                        onClose={() => setShowPostMoreOptions(false)}
                        onEdit={handleEditPost}
                        onDelete={handleDeletePost}
                        triggerRef={postMoreOptionsRef}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Back to Community Link */}
              <div className="mt-6">
                <button 
                  onClick={() => navigate('/community')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors flex items-center gap-1"
                >
                  <ArrowLeft size={14} />
                  Back to Community
                </button>
              </div>
            </DashboardCard>

            {/* Replies Section */}
            <DashboardCard>
              {discussionLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {replies.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-600 dark:text-slate-400">No replies yet. Be the first to join the conversation!</p>
                    </div>
                  ) : (
                    replies.map((reply) => {
                      return (
                        <ReplyItem
                          key={reply.id}
                          reply={reply}
                          discussion={discussion}
                          onLike={handleReplyLike}
                          onReply={handleReply}
                          onEdit={handleEditReply}
                          onDelete={handleDeleteReply}
                          currentUserId={user?.id}
                          isEditing={editingReplyId === reply.id}
                          editContent={editContent}
                          onEditContentChange={(content) => setEditContent(content)}
                          onSaveEdit={handleSaveEdit}
                          onCancelEdit={handleCancelEdit}
                          getUserName={getUserName}
                          getInitials={getInitials}
                        />
                      );
                    })
                  )}
                </div>
              )}
            </DashboardCard>

            {/* Reply Composer - Always visible */}
            <ReplyComposer 
              onSubmit={handleSubmitReply}
              mentionText={mentionText}
              onMentionAdded={() => setMentionText('')}
            />
          </div>
        </SidebarLayout>

        {/* Delete Reply Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteDialog}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          title="Delete Reply"
          description="Are you sure you want to delete this reply? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="red"
        />

        {/* Delete Post Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeletePostDialog}
          onConfirm={handleConfirmDeletePost}
          onCancel={handleCancelDeletePost}
          title="Delete Post"
          description="Are you sure you want to delete this entire post and all its replies? This action cannot be undone."
          confirmText="Delete Post"
          cancelText="Cancel"
          confirmVariant="red"
        />
        
        {/* Main Post Profile Lightbox */}
        {mainPostProfileLightboxImage && (
          <CommunityProfileLightbox
            currentImage={mainPostProfileLightboxImage}
            isLightboxOpen={isMainPostProfileLightboxOpen}
            profileContext={{
              user: {
                id: discussion?.author?.id || '',
                first_name: discussion?.author?.first_name || '',
                profile_picture_url: discussion?.author?.profile_picture_url
              },
              discussionId: id,
              isAuthor: true
            }}
            onClose={handleMainPostCloseLightbox}
          />
        )}
      </Container>
    </MobilePageWrapper>
  );
};

export default PostThread; 